const ACTIVE_QUERY_SELECTOR =
  '.cms-block-workspace-workspace-1-workspace-image-mention__activeQuery';
const MENU_GAP = 6;
const VIEWPORT_MARGIN = 8;
const MENU_MAX_HEIGHT = 240;

type Rectangle = Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right'>;

type ViewportBounds = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

/**
 * Anchor to the first character (@), not the end of the current search query.
 * Range geometry follows line wrapping and editor scroll without token offsets.
 */
export function readWorkspaceMentionAtRect(
  editor: HTMLElement
): Rectangle | null {
  const query = editor.querySelector<HTMLElement>(ACTIVE_QUERY_SELECTOR);
  if (!query) return null;

  const text = query.firstChild;
  if (
    text?.nodeType === Node.TEXT_NODE &&
    (text.textContent?.length ?? 0) > 0
  ) {
    const range = document.createRange();
    range.setStart(text, 0);
    range.setEnd(text, 1);
    if (typeof range.getBoundingClientRect === 'function') {
      const charRect = range.getBoundingClientRect();
      if (charRect.width > 0 && charRect.height > 0) {
        return charRect;
      }
    }
  }
  return query.getBoundingClientRect();
}

export function calculateWorkspaceMentionMenuPosition(params: {
  anchor: Rectangle;
  container: Rectangle;
  menuWidth: number;
  menuHeight: number;
  viewport: ViewportBounds;
}) {
  const { anchor, container, viewport } = params;
  const menuWidth = Math.max(0, params.menuWidth);
  const menuHeight = Math.min(MENU_MAX_HEIGHT, Math.max(0, params.menuHeight));

  const below = Math.max(
    0,
    viewport.bottom - anchor.bottom - MENU_GAP - VIEWPORT_MARGIN
  );
  const above = Math.max(
    0,
    anchor.top - viewport.top - MENU_GAP - VIEWPORT_MARGIN
  );
  const placeAbove = below < menuHeight && above > below;
  const availableHeight = placeAbove ? above : below;
  const height = Math.min(menuHeight, availableHeight);

  // The picker is rendered inside the Prompt wrapper, which can be
  // narrower than the viewport (sidebars, split panes, embedded workspaces).
  // Keep the whole popup inside BOTH available bounds instead of assuming
  // the browser viewport is the only possible clipping edge.
  const safeLeft = Math.max(
    viewport.left + VIEWPORT_MARGIN,
    container.left + VIEWPORT_MARGIN
  );
  const safeRight = Math.min(
    viewport.right - VIEWPORT_MARGIN,
    container.right - VIEWPORT_MARGIN
  );
  const maxWidth = Math.max(0, safeRight - safeLeft);
  const fittedWidth = Math.min(menuWidth, maxWidth);
  const maximumLeft = Math.max(safeLeft, safeRight - fittedWidth);
  const screenLeft = Math.min(Math.max(anchor.left, safeLeft), maximumLeft);
  const screenTop = placeAbove
    ? anchor.top - MENU_GAP - height
    : anchor.bottom + MENU_GAP;

  return {
    left: screenLeft - container.left,
    top: screenTop - container.top,
    maxHeight: Math.max(0, availableHeight),
    maxWidth,
    placement: placeAbove ? ('above' as const) : ('below' as const),
  };
}

function getViewportBounds(): ViewportBounds {
  const visualViewport = window.visualViewport;
  const left = visualViewport?.offsetLeft ?? 0;
  const top = visualViewport?.offsetTop ?? 0;
  const width = visualViewport?.width ?? window.innerWidth;
  const height = visualViewport?.height ?? window.innerHeight;
  return {
    top,
    left,
    right: left + width,
    bottom: top + height,
  };
}

/** Called after editor DOM commit and on scroll, resize or keyboard viewport changes. */
export function positionWorkspaceMentionMenu(params: {
  root: HTMLElement;
  editor: HTMLElement;
  menu: HTMLElement;
}) {
  const { root, editor, menu } = params;
  const anchor = readWorkspaceMentionAtRect(editor);
  if (!anchor) {
    menu.style.visibility = 'hidden';
    return;
  }

  // Clear the previous viewport clamp before measuring intrinsic width.
  // Otherwise a menu shrunk in a narrow split pane will be measured as
  // permanently narrow after the pane grows, and can overflow again.
  menu.style.maxWidth = '';
  const menuRect = menu.getBoundingClientRect();
  const placement = calculateWorkspaceMentionMenuPosition({
    anchor,
    container: root.getBoundingClientRect(),
    menuWidth: menuRect.width || menu.offsetWidth,
    menuHeight: Math.min(MENU_MAX_HEIGHT, menu.scrollHeight || menuRect.height),
    viewport: getViewportBounds(),
  });

  menu.style.left = placement.left + 'px';
  menu.style.top = placement.top + 'px';
  menu.style.maxHeight = Math.min(MENU_MAX_HEIGHT, placement.maxHeight) + 'px';
  menu.style.maxWidth = placement.maxWidth + 'px';
  menu.style.visibility = 'visible';
  menu.dataset.placement = placement.placement;
}
